import React from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { templateStatusOptions } from '@/components/assessment-engine/templates/templateStatusOptions';
import type { AeTemplateStatus } from '@/types/assessmentEngine';

const STATUS_SELECT_STYLE: Record<AeTemplateStatus, string> = {
  draft:
    'border-[var(--color-accent-bg)] text-[var(--color-accent-bg)] bg-[var(--color-accent-bg)]/10 focus:ring-[var(--color-accent-bg)]',
  published:
    'border-[var(--color-success)] text-[var(--color-success)] bg-[var(--color-success)]/10 focus:ring-[var(--color-success)]',
  retired:
    'border-[var(--color-warning)] text-[var(--color-warning)] bg-[var(--color-warning)]/10 focus:ring-[var(--color-warning)]',
};

const COMPACT_SELECT =
  'box-border h-8 shrink-0 rounded-md border bg-[var(--color-surface-bg)] px-1.5 pr-5 text-xs font-medium leading-none focus:outline-none focus:ring-1 disabled:cursor-not-allowed disabled:opacity-50';

export interface TemplateStatusSelectProps {
  templateName: string;
  status: AeTemplateStatus;
  selectKey: number;
  rowId: string;
  disabled?: boolean;
  onStatusChange: (next: AeTemplateStatus) => void;
}

/** Compact status dropdown aligned with list-row icon actions. */
export const TemplateStatusSelect: React.FC<TemplateStatusSelectProps> = ({
  templateName,
  status,
  selectKey,
  rowId,
  disabled,
  onStatusChange,
}) => {
  const { t } = useLanguage();
  const options = templateStatusOptions(status);

  return (
    <select
      key={`${rowId}-${selectKey}`}
      id={`template-status-${rowId}`}
      className={`${COMPACT_SELECT} ${STATUS_SELECT_STYLE[status]} w-auto`}
      defaultValue={status}
      disabled={disabled}
      aria-label={t('assessmentEngine.templates.statusLabel', { name: templateName })}
      onChange={(e) => onStatusChange(e.target.value as AeTemplateStatus)}
    >
      {options.map((optionStatus) => (
        <option key={optionStatus} value={optionStatus}>
          {t(`assessmentEngine.templateStatus.${optionStatus}`)}
        </option>
      ))}
    </select>
  );
};
